import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Brain, CheckCircle2, AlertTriangle,
  Moon, Activity, Heart, Smile, Wind, User2, Coffee, Save, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'mental_check_last_v2';

/**
 * Mental & Stress Health — gender-aware 4-step deep flow.
 *
 * Routes:
 *   /mental-health?context=women  (purple/violet palette)
 *   /mental-health?context=men    (slate/indigo palette)
 *
 * Mirrors HormonalHealth visual language so the user feels they're inside
 * one cohesive product. Output: a calm 'Mental band' (Steady / Stretched /
 * Overloaded) + top stressor signal + 3-5 grounded recommendations + a
 * crisis-safety banner that appears whenever the user reports persistent
 * very-low mood or hopelessness.
 */

const STEPS = ['stress', 'mood', 'sleep_energy', 'lifestyle', 'result'];

const STRESS_LEVELS = [
  { id: 'low', label: 'Low — mostly calm' },
  { id: 'moderate', label: 'Moderate — some pressure' },
  { id: 'high', label: 'High — feels constant' },
  { id: 'very_high', label: 'Very high — hard to switch off' },
];

const STRESS_SOURCES = [
  { id: 'work', label: 'Work / studies' },
  { id: 'finances', label: 'Finances' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'family', label: 'Family responsibilities' },
  { id: 'health', label: 'Health concerns' },
  { id: 'social', label: 'Social / loneliness' },
  { id: 'overload', label: 'Just too much at once' },
];

const MOOD_LEVELS = [
  { id: 'good', label: 'Mostly good' },
  { id: 'mixed', label: 'Mixed — ups and downs' },
  { id: 'low', label: 'Low / flat' },
  { id: 'very_low', label: 'Very low for 2+ weeks' },
];

const MOOD_FEELINGS = [
  { id: 'anxious', label: 'Anxious / on edge' },
  { id: 'irritable', label: 'Irritable' },
  { id: 'sad', label: 'Sad' },
  { id: 'numb', label: 'Numb / disconnected' },
  { id: 'overwhelmed', label: 'Overwhelmed' },
  { id: 'hopeless', label: 'Hopeless' },
  { id: 'restless', label: "Restless / can't focus" },
];

const SLEEP_OPTIONS = [
  { id: 'sleep_well', label: '7+ hours, restful', icon: Moon },
  { id: 'sleep_short', label: 'Less than 6 hours most nights', icon: Moon },
  { id: 'sleep_broken', label: 'Wake up multiple times', icon: Moon },
];

const ENERGY_OPTIONS = [
  { id: 'energy_steady', label: 'Steady through the day', icon: Activity },
  { id: 'energy_crash', label: 'Strong start, big afternoon crash', icon: Activity },
  { id: 'energy_low', label: 'Low all day', icon: Activity },
];

const LIFESTYLE_QUESTIONS = [
  { id: 'movement', label: 'I move my body most days', icon: Activity },
  { id: 'connection', label: 'I see / message someone close most days', icon: Heart },
  { id: 'reset', label: 'I have a 5-minute calm ritual I use', icon: Wind },
  { id: 'caffeine_ok', label: 'I keep caffeine to mornings only', icon: Coffee },
];

const computeProfile = ({ context, stress, sources, mood, feelings, sleep, energy, lifestyle }) => {
  // ---------- LOAD (the negative side of mental-stress signals) ----------
  let load = 0;
  load += { low: 0, moderate: 2, high: 4, very_high: 6 }[stress] || 0;
  load += Math.min(sources.length, 3); // up to +3 for stacking sources

  load += { good: 0, mixed: 2, low: 4, very_low: 7 }[mood] || 0;
  load += feelings.length;
  if (feelings.includes('hopeless') || feelings.includes('numb')) load += 2;

  if (sleep === 'sleep_short' || sleep === 'sleep_broken') load += 2;
  if (energy === 'energy_low') load += 2;
  if (energy === 'energy_crash') load += 1;

  load -= lifestyle.length; // up to -4 (protective rituals)
  load = Math.max(0, load);

  // ---------- WELLNESS SCORE (0-100, higher is better) ----------
  // Calibration: load=0 → 100, load=10 → 50, load=20+ → 0.
  // Crisis signals floor the score below 30 so the band is always honest.
  let score = Math.round(Math.max(0, 100 - load * 5));
  const crisisFlag = mood === 'very_low' || feelings.includes('hopeless');
  if (crisisFlag) score = Math.min(score, 25);

  // ---------- 4-BAND STATUS ----------
  // Calibrated to the user's spec: ≥85 Excellent · 70–84 Good · 50–69 Moderate · <50 Needs Attention.
  let band, status;
  if (score >= 85) { band = 'excellent'; status = 'Excellent'; }
  else if (score >= 70) { band = 'good'; status = 'Good'; }
  else if (score >= 50) { band = 'moderate'; status = 'Moderate'; }
  else { band = 'needs_attention'; status = 'Needs Attention'; }

  // ---------- TOP SIGNAL CLASSIFIER (priority-ordered, see header) ----------
  const topSignal = (() => {
    if (sources.includes('overload')) return 'Cognitive overload';
    if (feelings.includes('anxious') || feelings.includes('restless')) return 'Anxious arousal';
    if (mood === 'low' || mood === 'very_low') return 'Low mood';
    if (sleep === 'sleep_short' || sleep === 'sleep_broken') return 'Sleep-driven stress';
    if (sources.includes('work')) return 'Work pressure';
    if (sources.includes('finances')) return 'Financial pressure';
    if (sources.includes('relationships') || sources.includes('family')) return 'Relational load';
    if (sources.includes('social')) return 'Social isolation';
    return 'Background tension';
  })();

  const consultFlag = !crisisFlag && (band === 'needs_attention' || (mood === 'low' && feelings.length >= 3));

  // ---------- PERSONALISED INSIGHT MESSAGE ----------
  // References the user's actual answers — never generic.
  const insight = (() => {
    if (crisisFlag) {
      return "What you've described sounds heavy, and you don't have to carry it alone. The most important thing today isn't a habit — it's a conversation. Reach out to a GP, therapist, or someone you trust before the day ends.";
    }
    const protectorCount = lifestyle.length;
    const feelingCount = feelings.length;
    const sourceCount = sources.length;

    if (band === 'excellent') {
      const protectorBit = protectorCount >= 3
        ? `you have ${protectorCount} of 4 protective rituals already in place`
        : 'your foundations are quietly doing real work';
      return `You're in a strong place today — ${protectorBit}. ${topSignal} is the only soft note, and you're metabolising it well.`;
    }
    if (band === 'good') {
      return `You're coping well. ${topSignal} is showing up as your main pull, but ${protectorCount > 0 ? `${protectorCount} protective ritual${protectorCount === 1 ? '' : 's'} are buffering it` : 'a single daily ritual would tilt the balance further your way'}.`;
    }
    if (band === 'moderate') {
      const detail = feelingCount > 0
        ? `${feelingCount} recurring feeling${feelingCount === 1 ? '' : 's'} are stacking with`
        : sourceCount > 0
          ? 'pressure is stacking with'
          : 'load is building under';
      return `You're stretched. ${detail} ${topSignal.toLowerCase()} — small daily resets will keep this from tipping over. Pick ONE step from the list below today.`;
    }
    // needs_attention
    return `This is a heavy week — ${topSignal.toLowerCase()} is loud, and ${feelingCount > 0 ? `you flagged ${feelingCount} recurring feeling${feelingCount === 1 ? '' : 's'}` : 'the weight is real'}. Be gentle. The win today is one small reset that compounds, not fixing everything at once.`;
  })();

  // ---------- RECOMMENDATIONS — specific, daily, action-first (cap 3-5) ----------
  // Each rec is phrased as a concrete, time-anchored daily action so the user
  // can do it today without thinking. Generic advice is avoided.
  const recs = [];
  if (crisisFlag) {
    recs.push("Today: text or call one person you trust and tell them you're not okay. The first sentence is the hardest — say it now while you have momentum.");
    recs.push("If anything feels unsafe, contact a crisis line right now. UK: Samaritans 116 123. US: 988. They're trained for exactly this.");
    recs.push("Tonight: skip alcohol and late-night news/social feeds — both deepen low-mood loops within hours.");
  } else {
    const candidates = [];

    // Stress-driven
    if (stress === 'very_high') {
      candidates.push("Right now (2 minutes): box-breathe — inhale 4, hold 4, exhale 6, hold 4. Five rounds drops cortisol enough to think clearly again.");
    } else if (stress === 'high') {
      candidates.push("Twice today (1 minute each): inhale through the nose for 4, exhale through pursed lips for 6. Long exhales tell your body the threat passed.");
    }

    // Mood / feelings
    if (feelings.includes('anxious') || feelings.includes('restless')) {
      candidates.push("Today before 4pm: walk for 12 minutes outdoors. Sunlight + rhythm together resolve anxious energy faster than any single rest.");
    }
    if (feelings.includes('overwhelmed') || sources.includes('overload')) {
      candidates.push("In the next hour: brain-dump everything on your mind for 10 minutes onto paper. Not to solve — just to externalise. The list is always smaller than the spiral.");
    }
    if (feelings.includes('sad') || mood === 'low') {
      candidates.push("Today: do one small thing you enjoyed last week — a song, a meal, a 5-minute hobby. Pleasure is data; low mood lies about it.");
    }
    if (feelings.includes('irritable')) {
      candidates.push("Tonight: name one specific trigger from today out loud (or in a note). Naming defuses irritability faster than ignoring it.");
    }

    // Sleep
    if (sleep === 'sleep_short') {
      candidates.push("Tonight: in bed by 22:30 with screens off 30 minutes earlier. One night of 7 hours rebalances mood within 24 hours.");
    } else if (sleep === 'sleep_broken') {
      candidates.push("Tonight: same wake time tomorrow no matter what — even on weekends. A consistent wake time is the single biggest fix for broken sleep.");
    }

    // Energy
    if (energy === 'energy_low' && !candidates.some((c) => c.toLowerCase().includes('walk'))) {
      candidates.push("This morning (10 minutes): step outside in daylight before 11am. Morning light is the strongest, free, evidence-backed energy reset.");
    }
    if (energy === 'energy_crash') {
      candidates.push("At lunch: pair protein with vegetables (skip the sweet drink). The afternoon crash is mostly a glucose curve — flatten it and the slump softens.");
    }

    // Lifestyle gaps
    if (!lifestyle.includes('connection')) {
      candidates.push("Today before noon: send one short voice note or text to a friend. Connection is the single strongest mental-health buffer in the research.");
    }
    if (!lifestyle.includes('movement')) {
      candidates.push("Today: 15 minutes of any movement — walk, stretch, dance in the kitchen. Light movement lifts mood within 60 minutes, every time.");
    }
    if (!lifestyle.includes('reset')) {
      candidates.push("Pick one calm ritual today (5 minutes: breath, stretch, tea, music) and put it in tomorrow's calendar at the same time.");
    }
    if (!lifestyle.includes('caffeine_ok')) {
      candidates.push("Last coffee before noon today. Caffeine half-life is 5–6 hours — afternoon coffee fragments tonight's deep sleep, which fragments tomorrow's mood.");
    }

    // Source-specific
    if (sources.includes('work') && !candidates.some((c) => c.toLowerCase().includes('walk'))) {
      candidates.push("Block one 25-minute work block today with no notifications. One unbroken block is more recovery than three fragmented hours.");
    }
    if (sources.includes('relationships') || sources.includes('family')) {
      candidates.push("Today: have one honest conversation about one specific thing — small, named, soon. Resentment compounds; conversations break the loop.");
    }

    // Always end with a short anchor
    if (band === 'excellent' || band === 'good') {
      candidates.push("This week: keep your strongest current ritual exactly as it is. Consistency, not novelty, is what built this band.");
    }

    // Fallbacks if too few (rare)
    while (candidates.length < 3) {
      const fallback = [
        "Today (5 minutes): step outside in daylight. Free, fast, evidence-backed mood lift.",
        "Right now: drink one full glass of water. Hydration steadies focus and the nervous system.",
        "Today: do one thing slowly without a screen — eating, walking, or breathing.",
      ].find((c) => !candidates.includes(c));
      if (!fallback) break;
      candidates.push(fallback);
    }
    recs.push(...candidates.slice(0, 5));
  }

  // ---------- CLOSING THOUGHT — feels like a conclusion, not just output ----------
  const closing = crisisFlag
    ? "You showed up to this check-in on a heavy day — a small act of care in itself. Reach out to one person you trust today. That's the only step that matters right now."
    : band === 'excellent'
    ? "Your signals point to a steady week. The work now is gentle: keep what's working and notice the small things you'd miss otherwise."
    : band === 'good'
    ? "You're managing this well. One small ritual repeated daily is what moves a 'Good' week into an 'Excellent' month."
    : band === 'moderate'
    ? "You're carrying real weight, and you're still showing up. Pick ONE action above for today — not all of them. Tomorrow's check-in is where you'll see the shift."
    : "Heavy weeks pass. The fact that you ran this check-in is already a small act of care for yourself. One thing today. That's enough.";

  return { score, band, status, topSignal, insight, recs, closing, crisisFlag, consultFlag, load };
};

const BAND_STYLE = {
  excellent:       { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'stroke-emerald-500' },
  good:            { text: 'text-teal-700',    bg: 'bg-teal-50',    border: 'border-teal-200',    ring: 'stroke-teal-500' },
  moderate:        { text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200',   ring: 'stroke-amber-500' },
  needs_attention: { text: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200',    ring: 'stroke-rose-500' },
};

const MentalHealth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const context = searchParams.get('context') === 'men' ? 'men' : 'women';

  const [stepIdx, setStepIdx] = useState(0);
  const [stress, setStress] = useState('');
  const [sources, setSources] = useState([]);
  const [mood, setMood] = useState('');
  const [feelings, setFeelings] = useState([]);
  const [sleep, setSleep] = useState('');
  const [energy, setEnergy] = useState('');
  const [lifestyle, setLifestyle] = useState([]);

  // Persistence state (session save).
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [savedAt, setSavedAt] = useState(null);
  // Snapshot of the PREVIOUS check (read once on result-step entry, before
  // we overwrite localStorage with the new one). null = first-ever check.
  const [previous, setPrevious] = useState(null);

  const step = STEPS[stepIdx];
  const progress = useMemo(() => ((stepIdx + 1) / STEPS.length) * 100, [stepIdx]);

  const palette = context === 'women'
    ? { from: 'from-purple-500', to: 'to-violet-500', accent: 'text-purple-700', soft: 'bg-purple-50', activeBorder: 'border-purple-500 bg-purple-50 text-purple-900', hoverBorder: 'hover:border-purple-300', tagText: 'text-purple-700' }
    : { from: 'from-indigo-500', to: 'to-slate-700', accent: 'text-indigo-700', soft: 'bg-indigo-50', activeBorder: 'border-indigo-500 bg-indigo-50 text-indigo-900', hoverBorder: 'hover:border-indigo-300', tagText: 'text-indigo-700' };

  const toggle = (list, setList, id) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const canAdvance = () => {
    if (step === 'stress') return stress !== '';
    if (step === 'mood') return mood !== '';
    if (step === 'sleep_energy') return sleep !== '' && energy !== '';
    return true;
  };

  const next = () => canAdvance() && setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const result = useMemo(() => {
    if (step !== 'result') return null;
    return computeProfile({ context, stress, sources, mood, feelings, sleep, energy, lifestyle });
  }, [step, context, stress, sources, mood, feelings, sleep, energy, lifestyle]);

  // Persist result to user session — localStorage immediately + backend best-effort.
  // Runs once when the user lands on the result step. Reads the PREVIOUS
  // record FIRST (for the 'compared to last check' card) before overwriting.
  useEffect(() => {
    if (!result || saveState !== 'idle') return;

    // 1) Read previous check from localStorage BEFORE we overwrite.
    try {
      const prevRaw = localStorage.getItem(SESSION_KEY);
      if (prevRaw) {
        const prev = JSON.parse(prevRaw);
        if (prev && typeof prev.score === 'number') setPrevious(prev);
      }
    } catch (_) { /* corrupt or quota — ignore */ }

    const payload = {
      context,
      score: result.score,
      status: result.status,
      band: result.band,
      top_signal: result.topSignal,
      insight: result.insight,
      recommendations: result.recs,
      inputs: { stress, sources, mood, feelings, sleep, energy, lifestyle },
      crisis_flag: result.crisisFlag,
      consult_flag: result.consultFlag,
    };
    // 2) Local session cache — store ONLY the comparison-essential fields.
    //    We deliberately do NOT cache inputs / insight / recommendations /
    //    crisis_flag client-side: those live server-side and are sensitive.
    try {
      const cacheable = {
        score: result.score,
        status: result.status,
        band: result.band,
        saved_at: new Date().toISOString(),
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(cacheable));
    } catch (_) { /* quota / private mode — best-effort */ }
    setSaveState('saving');
    axios
      .post(`${API}/mental-checks`, payload, { withCredentials: true })
      .then((res) => {
        setSaveState('saved');
        setSavedAt(res?.data?.saved_at || new Date().toISOString());
      })
      .catch(() => {
        // localStorage already saved — degrade gracefully.
        setSaveState('error');
      });
  }, [result, saveState, context, stress, sources, mood, feelings, sleep, energy, lifestyle]);

  const homeRoute = context === 'women' ? '/womens-health' : '/mens-health';
  const homeLabel = context === 'women' ? "Women's Health" : "Men's Health";

  return (
    <div className={`min-h-screen bg-gradient-to-b ${context === 'women' ? 'from-purple-50/40' : 'from-indigo-50/40'} via-white to-white pb-24`} data-testid="mental-health-flow">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => (stepIdx === 0 ? navigate(homeRoute) : back())}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4"
          data-testid="mental-back"
        >
          <ArrowLeft className="h-4 w-4" />
          {stepIdx === 0 ? `Back to ${homeLabel}` : 'Back'}
        </button>

        <header className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${palette.from} ${palette.to} flex items-center justify-center shrink-0`}>
              <Brain className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight" data-testid="mental-title">
                Mental &amp; Stress Health
              </h1>
              <p className="text-xs text-gray-500 mt-0.5 inline-flex items-center gap-1.5">
                <User2 className="h-3 w-3" />
                {context === 'women' ? "Women's flow" : "Men's flow"} · 60-second snapshot. Not a diagnosis.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                {step === 'result' ? 'Result' : `Step ${stepIdx + 1} of ${STEPS.length - 1}`}
              </span>
              <span className="text-[11px] font-semibold text-gray-400 tracking-wide">
                {step === 'stress' && 'Stress'}
                {step === 'mood' && 'Mood'}
                {step === 'sleep_energy' && 'Sleep & Energy'}
                {step === 'lifestyle' && 'Calm rituals'}
                {step === 'result' && 'Your snapshot'}
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${palette.from} ${palette.to} transition-all duration-500 ease-out`}
                style={{ width: `${progress}%` }}
                data-testid="mental-progress"
              />
            </div>
          </div>
        </header>

        {/* Animated step container — keyed by step so the user gets a soft
            slide+fade between transitions, not a hard cut. */}
        <div key={step} className="animate-in fade-in slide-in-from-bottom-2 duration-300">

        {step === 'stress' && (
          <section className="space-y-4" data-testid="mental-step-stress">
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 1 of 4</p>
              <h2 className="text-lg font-bold text-gray-900 mb-1">How does your stress feel right now?</h2>
              <p className="text-sm text-gray-600 mb-4">Pick the line closest to your last week.</p>
              <div className="space-y-2">
                {STRESS_LEVELS.map((opt) => {
                  const active = stress === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setStress(opt.id)}
                      className={`w-full text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                        active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder} text-gray-700`
                      }`}
                      data-testid={`mental-stress-${opt.id}`}
                    >
                      {active && <CheckCircle2 className={`h-4 w-4 inline mr-1.5 ${palette.accent}`} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <h3 className="text-base font-bold text-gray-900 mb-1">Where is most of it coming from?</h3>
              <p className="text-sm text-gray-600 mb-3">Multi-select — skip if none stand out.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {STRESS_SOURCES.map((opt) => {
                  const active = sources.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => toggle(sources, setSources, opt.id)}
                      className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                        active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder} text-gray-700`
                      }`}
                      data-testid={`mental-source-${opt.id}`}
                    >
                      {active && <CheckCircle2 className={`h-4 w-4 inline mr-1.5 ${palette.accent}`} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {step === 'mood' && (
          <section className="space-y-4" data-testid="mental-step-mood">
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 2 of 4</p>
              <h2 className="text-lg font-bold text-gray-900 mb-1">How has your mood been?</h2>
              <p className="text-sm text-gray-600 mb-4">Across the past two weeks.</p>
              <div className="space-y-2">
                {MOOD_LEVELS.map((opt) => {
                  const active = mood === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setMood(opt.id)}
                      className={`w-full text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                        active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder} text-gray-700`
                      }`}
                      data-testid={`mental-mood-${opt.id}`}
                    >
                      {active && <CheckCircle2 className={`h-4 w-4 inline mr-1.5 ${palette.accent}`} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <h3 className="text-base font-bold text-gray-900 mb-1">Any feelings recurring?</h3>
              <p className="text-sm text-gray-600 mb-3">Multi-select — skip if none.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {MOOD_FEELINGS.map((opt) => {
                  const active = feelings.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => toggle(feelings, setFeelings, opt.id)}
                      className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                        active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder} text-gray-700`
                      }`}
                      data-testid={`mental-feeling-${opt.id}`}
                    >
                      {active && <CheckCircle2 className={`h-4 w-4 inline mr-1.5 ${palette.accent}`} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {step === 'sleep_energy' && (
          <section className="space-y-4" data-testid="mental-step-sleep-energy">
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 3 of 4</p>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Sleep this week</h2>
              <p className="text-sm text-gray-600 mb-4">Choose the closest match.</p>
              <div className="space-y-2">
                {SLEEP_OPTIONS.map((opt) => {
                  const Oi = opt.icon;
                  const active = sleep === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setSleep(opt.id)}
                      className={`w-full text-left flex items-center gap-3 rounded-xl border-2 px-3 py-3 transition-all ${
                        active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder}`
                      }`}
                      data-testid={`mental-sleep-${opt.id}`}
                    >
                      <span className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                        <Oi className="h-4 w-4 text-gray-700" />
                      </span>
                      <span className="text-sm font-medium text-gray-800 flex-1">{opt.label}</span>
                      {active && <CheckCircle2 className={`h-4 w-4 ${palette.accent}`} />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <h3 className="text-base font-bold text-gray-900 mb-1">Energy through the day</h3>
              <p className="text-sm text-gray-600 mb-3">Choose the closest match.</p>
              <div className="space-y-2">
                {ENERGY_OPTIONS.map((opt) => {
                  const Oi = opt.icon;
                  const active = energy === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setEnergy(opt.id)}
                      className={`w-full text-left flex items-center gap-3 rounded-xl border-2 px-3 py-3 transition-all ${
                        active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder}`
                      }`}
                      data-testid={`mental-energy-${opt.id}`}
                    >
                      <span className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                        <Oi className="h-4 w-4 text-gray-700" />
                      </span>
                      <span className="text-sm font-medium text-gray-800 flex-1">{opt.label}</span>
                      {active && <CheckCircle2 className={`h-4 w-4 ${palette.accent}`} />}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {step === 'lifestyle' && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="mental-step-lifestyle">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 4 of 4</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Calm rituals you already have</h2>
            <p className="text-sm text-gray-600 mb-4">Tick what's already true for you.</p>
            <div className="space-y-2">
              {LIFESTYLE_QUESTIONS.map((q) => {
                const Qi = q.icon;
                const active = lifestyle.includes(q.id);
                return (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => toggle(lifestyle, setLifestyle, q.id)}
                    className={`w-full text-left flex items-center gap-3 rounded-xl border-2 px-3 py-3 transition-all ${
                      active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder}`
                    }`}
                    data-testid={`mental-lifestyle-${q.id}`}
                  >
                    <span className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                      <Qi className="h-4 w-4 text-gray-700" />
                    </span>
                    <span className="text-sm font-medium text-gray-800 flex-1">{q.label}</span>
                    {active && <CheckCircle2 className={`h-4 w-4 ${palette.accent}`} />}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 'result' && result && (
          <section data-testid="mental-result" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
            {result.crisisFlag && (
              <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 mb-4 flex gap-3" data-testid="mental-crisis-flag">
                <AlertTriangle className="h-5 w-5 text-rose-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-rose-900">Please reach out today</p>
                  <p className="text-sm text-rose-800 mt-1 leading-relaxed">
                    What you described is heavy and you don't have to carry it alone. Talk to a GP, therapist, or someone you trust today.
                    If you ever feel unsafe, contact a crisis line in your country immediately —
                    UK: <strong>Samaritans 116 123</strong> · US: <strong>988</strong>.
                  </p>
                </div>
              </div>
            )}

            {result.consultFlag && !result.crisisFlag && (
              <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 mb-4 flex gap-3" data-testid="mental-consult-flag">
                <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-900">Worth talking to a clinician</p>
                  <p className="text-sm text-amber-800 mt-1">
                    Several signals are stacking. A short conversation with your GP, a therapist, or your employer's
                    EAP can be a real lever. This tool is not a diagnosis.
                  </p>
                </div>
              </div>
            )}

            {/* Score ring + status */}
            <div className={`rounded-2xl border-2 ${BAND_STYLE[result.band].border} ${BAND_STYLE[result.band].bg} p-5 mb-4 flex items-center gap-5`}>
              <div className="relative w-24 h-24 shrink-0" data-testid="mental-score-ring">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="9" />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    fill="none"
                    strokeWidth="9"
                    strokeLinecap="round"
                    className={`${BAND_STYLE[result.band].ring} transition-all duration-700 ease-out`}
                    style={{ strokeDasharray: 2 * Math.PI * 42, strokeDashoffset: 2 * Math.PI * 42 * (1 - result.score / 100) }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-gray-900 leading-none" data-testid="mental-score-value">{result.score}</span>
                  <span className="text-[10px] font-medium text-gray-500 mt-0.5">/ 100</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Your mental wellness</p>
                <h2 className="text-2xl font-bold text-gray-900" data-testid="mental-status">
                  <span className={BAND_STYLE[result.band].text}>{result.status}</span>
                </h2>
                <p className="text-xs text-gray-600 mt-1">
                  Top signal: <span className="font-semibold">{result.topSignal}</span>
                </p>
              </div>
            </div>

            {/* Compared-to-last-check card (only when a previous check exists) */}
            {previous && typeof previous.score === 'number' && (() => {
              const delta = result.score - previous.score;
              const since = (() => {
                if (!previous.saved_at) return 'last check';
                const ms = Date.now() - new Date(previous.saved_at).getTime();
                const days = Math.floor(ms / (1000 * 60 * 60 * 24));
                if (days < 1) return 'earlier today';
                if (days === 1) return 'yesterday';
                if (days < 7) return `${days} days ago`;
                if (days < 30) return `${Math.floor(days / 7)} week${days < 14 ? '' : 's'} ago`;
                return `${Math.floor(days / 30)} month${days < 60 ? '' : 's'} ago`;
              })();
              const trend = delta >= 4 ? 'up' : delta <= -4 ? 'down' : 'flat';
              const trendStyle = trend === 'up'
                ? { dot: 'bg-emerald-500', text: 'text-emerald-700', label: `Up ${delta} since ${since}`, msg: "Your trajectory is moving the right way. Whatever you've been doing — keep doing it." }
                : trend === 'down'
                ? { dot: 'bg-rose-500', text: 'text-rose-700', label: `Down ${Math.abs(delta)} since ${since}`, msg: 'Something has shifted. The recommendations below are tuned for where you are right now.' }
                : { dot: 'bg-gray-400', text: 'text-gray-700', label: `Steady (±${Math.abs(delta)}) since ${since}`, msg: "You're holding your ground. Small, repeated rituals are what move 'steady' into 'rising'." };
              return (
                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4 mb-4 flex items-start gap-3" data-testid="mental-compare">
                  <span className={`w-2.5 h-2.5 rounded-full ${trendStyle.dot} mt-1.5 shrink-0`} aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="flex flex-wrap items-baseline gap-x-2">
                      <span className={`text-sm font-bold ${trendStyle.text}`} data-testid="mental-compare-delta">{trendStyle.label}</span>
                      <span className="text-[11px] text-gray-400">· previous: {previous.score}/100 · {previous.status}</span>
                    </p>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{trendStyle.msg}</p>
                  </div>
                </div>
              );
            })()}

            {/* Personalised insight message */}
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4" data-testid="mental-insight">
              <p className={`text-[11px] font-semibold ${palette.tagText} uppercase tracking-widest mb-2`}>Insight for you</p>
              <p className="text-base text-gray-900 font-medium leading-relaxed">{result.insight}</p>
            </div>

            {/* Recommendations (3-5, capped) */}
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">Try this today</p>
              <ul className="space-y-2.5" data-testid="mental-recs">
                {result.recs.map((r) => (
                  <li key={r} className="flex items-start gap-2.5">
                    <CheckCircle2 className={`h-4 w-4 ${palette.accent} shrink-0 mt-0.5`} />
                    <p className="text-sm text-gray-700 leading-relaxed">{r}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Save indicator */}
            <div className="flex items-center justify-center gap-1.5 mb-4 text-xs" data-testid="mental-save-state" data-state={saveState}>
              {saveState === 'saving' && (
                <span className="inline-flex items-center gap-1.5 text-gray-500"><Loader2 className="h-3 w-3 animate-spin" /> Saving to your session…</span>
              )}
              {saveState === 'saved' && (
                <span className="inline-flex items-center gap-1.5 text-emerald-700"><Save className="h-3 w-3" /> Saved to your session{savedAt ? '' : ''}</span>
              )}
              {saveState === 'error' && (
                <span className="inline-flex items-center gap-1.5 text-amber-700"><Save className="h-3 w-3" /> Saved locally — we'll retry sync next time</span>
              )}
            </div>

            {/* Closing thought — feels like a conclusion, not just output */}
            <div
              className={`rounded-2xl border-2 ${BAND_STYLE[result.band].border} ${BAND_STYLE[result.band].bg} p-5 mb-4`}
              data-testid="mental-closing"
            >
              <p className={`text-[11px] font-semibold ${BAND_STYLE[result.band].text} uppercase tracking-widest mb-2`}>One last thing</p>
              <p className="text-sm text-gray-800 leading-relaxed font-medium">{result.closing}</p>
            </div>

            <p className="text-[11px] text-gray-400 text-center mb-4">
              HealthLoop supports awareness — it does not diagnose or replace professional mental-health care.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className={`bg-gradient-to-br ${palette.from} ${palette.to} text-white font-semibold rounded-xl px-5 py-3 shadow-sm transition-all`}
                data-testid="mental-cta-dashboard"
              >
                Back to dashboard →
              </button>
              <button
                onClick={() => {
                  setStepIdx(0); setStress(''); setSources([]); setMood('');
                  setFeelings([]); setSleep(''); setEnergy(''); setLifestyle([]);
                  setSaveState('idle'); setSavedAt(null); setPrevious(null);
                }}
                className="bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-xl px-5 py-3 border border-gray-200 transition-all"
                data-testid="mental-cta-restart"
              >
                Check again
              </button>
            </div>
          </section>
        )}

        {step !== 'result' && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={next}
              disabled={!canAdvance()}
              className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm transition-all ${
                canAdvance()
                  ? `bg-gradient-to-br ${palette.from} ${palette.to} hover:brightness-110 text-white`
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="mental-next"
            >
              {stepIdx === STEPS.length - 2 ? 'Get my snapshot' : 'Continue'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default MentalHealth;
